/**
 * Company Controls (ADR-022): the umbrella over workspaces. The page is a
 * shell — the Company switcher, the Create wizard and a side navigation —
 * around one section at a time (`./company/*`): General (name, payer,
 * Danger zone), People (the owner/admin roster), Workspaces (links),
 * Arche approvals, Billing. Both the Company and the section live in the
 * URL (`/company/:companyId/:section`), so a refresh, the back button and a
 * pasted link land on the same view. Adding a section is one entry in
 * `COMPANY_SECTIONS` plus one component. Branding, Email and Web appear only for a
 * Company Fias has granted them; a link to one from a Company without the
 * grant lands on General.
 *
 * Every Company route is session-only and MFA-checked server-side; the page
 * surfaces `MFA_REQUIRED_FOR_COMPANY` as a plain instruction rather than a
 * failure.
 *
 * Open seat invitations the viewer has RECEIVED are shown above everything
 * (a seat is held only once accepted), so someone invited lands here and
 * can answer.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '../hooks/useAuth';
import { SettingsSideNav } from '@/components/SettingsSideNav';
import { useCompany, useMyCompanies, useMyCompanySeatInvitations } from '../hooks/useCompanies';
import { CompanySeatInvitationsList } from '../components/AccountAlerts';
import { CompanyExplainer } from '../components/CompanyExplainer';
import { CreateCompanyWizard } from '../components/CreateCompanyWizard';
import {
  DEFAULT_COMPANY_SECTION,
  companySectionPath,
  describeError,
  isCompanySectionId,
  visibleCompanySections,
  type CompanySectionId,
} from './company/shared';
import { GeneralSection } from './company/GeneralSection';
import { PeopleSection } from './company/PeopleSection';
import { WorkspacesSection } from './company/WorkspacesSection';
import { ApprovalsSection } from './company/ApprovalsSection';
import { BillingSection } from './company/BillingSection';
import { OwnedArchesSection } from './company/OwnedArchesSection';
import { CompanyBrandingCard } from '../components/CompanyBrandingCard';
import { CompanyEmailCard } from '../components/CompanyEmailCard';
import { CompanyWebCard } from '../components/CompanyWebCard';

/** Last Company opened on this device — the fallback when landing on bare /company. */
const LAST_COMPANY_KEY = 'fias.company.last-selected';
function readLastCompany(): string | null {
  try {
    return localStorage.getItem(LAST_COMPANY_KEY);
  } catch {
    return null;
  }
}
function rememberCompany(companyId: string | null): void {
  try {
    if (companyId) localStorage.setItem(LAST_COMPANY_KEY, companyId);
    else localStorage.removeItem(LAST_COMPANY_KEY);
  } catch {
    // per-viewer convenience only
  }
}

/** Seat invitations the viewer has received; nothing when there are none. */
function SeatInvitationsCard() {
  const { isAuthenticated, isGuest } = useAuth();
  const invitations = useMyCompanySeatInvitations({ enabled: isAuthenticated && !isGuest });
  if ((invitations.data?.length ?? 0) === 0) return null;
  return (
    <Card className="border-primary/40 bg-primary/5">
      <CardHeader>
        <CardTitle className="text-lg">Company invitations</CardTitle>
        <CardDescription>
          You have been offered a seat on these Companies. You hold it only once you accept.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <CompanySeatInvitationsList />
      </CardContent>
    </Card>
  );
}

export function CompanyControlsPage() {
  const companies = useMyCompanies();
  const navigate = useNavigate();
  const { companyId: routeCompanyId, section: routeSection } = useParams<{
    companyId?: string;
    section?: string;
  }>();
  const [wizardOpen, setWizardOpen] = useState(false);
  const list = companies.data ?? [];
  const has = (id: string | null | undefined) =>
    !!id && list.some((c) => c.company.companyId === id);
  // URL wins; then the last one opened here; then the first the user holds.
  const activeId = has(routeCompanyId)
    ? routeCompanyId!
    : has(readLastCompany())
      ? readLastCompany()!
      : (list[0]?.company.companyId ?? null);
  const active = list.find((c) => c.company.companyId === activeId) ?? null;
  // The same query CompanyDetail reads (React Query dedupes): the nav needs
  // the Company's grants to know which sections to offer.
  const activeSettings = useCompany(activeId);
  const sections = visibleCompanySections(activeSettings.data?.entitlements);
  const section: CompanySectionId = isCompanySectionId(routeSection)
    ? routeSection
    : DEFAULT_COMPANY_SECTION;
  const setSelectedId = (id: string | null) => {
    rememberCompany(id);
    navigate(id ? companySectionPath(id, section) : '/company', { replace: true });
  };
  // Keep the URL and the remembered choice in step with what is shown: a
  // stale id (a Company deleted or left) is dropped from both, and a missing
  // or unknown section becomes the explicit default so the navigation can
  // mark it.
  useEffect(() => {
    // While the list is (re)fetching, a route id the stale list lacks may be
    // a Company created a moment ago: leave the URL alone until it settles.
    if (companies.isLoading || companies.isFetching) return;
    if (activeId) {
      rememberCompany(activeId);
      if (routeCompanyId !== activeId || !isCompanySectionId(routeSection)) {
        navigate(companySectionPath(activeId, section), { replace: true });
      }
    } else if (routeCompanyId) {
      navigate('/company', { replace: true });
    }
  }, [
    activeId,
    routeCompanyId,
    routeSection,
    section,
    companies.isLoading,
    companies.isFetching,
    navigate,
  ]);

  return (
    <div>
      <div className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Company</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              One place for billing, administration, arche approvals and branding across the
              workspaces a Company links. A Company holds no data of its own.
            </p>
          </div>
        </div>
      </div>
      <CreateCompanyWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        onCreated={setSelectedId}
      />
      <div className="max-w-6xl mx-auto px-4 py-8 sm:px-6 lg:px-8 space-y-6">
        <SeatInvitationsCard />
        {/* With a Company in hand: the switcher (when there are several) or
            the Company's name on the left, Create on the right. With none,
            the explainer's call to action is the only door. */}
        {!companies.isLoading && !companies.isError && list.length > 0 && (
          <div className="flex flex-wrap items-end justify-between gap-3">
            {list.length > 1 ? (
              <div className="grid min-w-[16rem] max-w-md flex-1 gap-1.5">
                <Label htmlFor="company-switcher">Company</Label>
                <select
                  id="company-switcher"
                  className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                  value={activeId ?? ''}
                  onChange={(e) => setSelectedId(e.target.value || null)}
                >
                  {list.map(({ company, role }) => (
                    <option key={company.companyId} value={company.companyId}>
                      {company.name} — {role}
                    </option>
                  ))}
                </select>
              </div>
            ) : active ? (
              <h2 className="flex items-center gap-2 text-xl font-semibold text-foreground">
                {active.company.name}
                <Badge variant="secondary" className="capitalize">
                  {active.role}
                </Badge>
              </h2>
            ) : (
              <span />
            )}
            <Button onClick={() => setWizardOpen(true)}>Create new Company</Button>
          </div>
        )}
        {/* First visit: explain the model before asking for a name. */}
        {!companies.isLoading && !companies.isError && list.length === 0 && (
          <CompanyExplainer onCreate={() => setWizardOpen(true)} />
        )}
        {companies.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : companies.isError ? (
          <p className="text-sm text-destructive">{describeError(companies.error)}</p>
        ) : activeId ? (
          <div className="md:grid md:grid-cols-[13rem_minmax(0,1fr)] md:items-start md:gap-8">
            <SettingsSideNav
              label="Company sections"
              className="mb-4 md:sticky md:top-4 md:mb-0"
              items={sections.map((s) => ({
                to: companySectionPath(activeId, s.id),
                label: s.label,
                icon: s.icon,
              }))}
            />
            <div className="min-w-0 space-y-6">
              <CompanyDetail
                key={activeId}
                companyId={activeId}
                section={section}
                onDeleted={() => setSelectedId(null)}
              />
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            You are not an owner or admin of any Company yet. If a Company invited one of your
            workspaces, accept it under Workspace Controls.
          </p>
        )}
      </div>
    </div>
  );
}

function CompanyDetail({
  companyId,
  section,
  onDeleted,
}: {
  companyId: string;
  section: CompanySectionId;
  onDeleted: () => void;
}) {
  const query = useCompany(companyId);
  const navigate = useNavigate();
  // A gated section this Company does not hold (a pasted link, or switching
  // Companies while on it): go to General instead of showing an empty page.
  const hidden =
    !!query.data && !visibleCompanySections(query.data.entitlements).some((s) => s.id === section);
  useEffect(() => {
    if (hidden) navigate(companySectionPath(companyId, DEFAULT_COMPANY_SECTION), { replace: true });
  }, [hidden, companyId, navigate]);
  if (query.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (query.isError || !query.data) {
    return <p className="text-sm text-destructive">{describeError(query.error)}</p>;
  }
  if (hidden) return null;
  const data = query.data;
  const isOwner = data.myRole === 'owner';
  switch (section) {
    case 'general':
      return <GeneralSection data={data} isOwner={isOwner} onDeleted={onDeleted} />;
    case 'people':
      return <PeopleSection data={data} isOwner={isOwner} />;
    case 'workspaces':
      return <WorkspacesSection data={data} isOwner={isOwner} />;
    case 'approvals':
      return <ApprovalsSection data={data} isOwner={isOwner} />;
    case 'arches':
      return <OwnedArchesSection data={data} isOwner={isOwner} />;
    case 'billing':
      return <BillingSection companyId={companyId} />;
    case 'branding':
      return <CompanyBrandingCard companyId={companyId} />;
    case 'email':
      return <CompanyEmailCard companyId={companyId} />;
    case 'web':
      return <CompanyWebCard companyId={companyId} />;
  }
}
